/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import {
    FormControl,
    Typography,
    Select,
    MenuItem,
    LinearProgress,
    Fade,
    Checkbox,
    ListItemText,
    List,
    ListItem,
    ListItemIcon,
    Button,
    TextField,
    Box,
    Grid,
    Snackbar,
    Card,
    Collapse,
    FormLabel,
} from "@material-ui/core/";

import moment, { Moment } from "moment-timezone";
import { RouteComponentProps } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DatePicker, MuiPickersUtilsProvider, TimePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import MomentUtils from "@date-io/moment";
import { useVideExportRequest, useVideoExportData } from "src/hooks/useVideoExportData";
import { useAuth } from "src/contexts/AuthStateContext";
import { VideoExportPost } from "src/entities/VideoExportData";
import MuiAlert, { Color } from "@material-ui/lab/Alert";
import "./_video-export.scss";
import { Alert, Skeleton } from "@material-ui/lab";
import DOMPurify from "dompurify";
import { Profile } from "src/types";
import { usesAmPm } from "src/services/userProfileService";
import { Site } from "../../../entities";

type Props = {
    site: Site;
    userProfile: Profile;
} & RouteComponentProps;

export const VideoExport = (props: Props) => {
    const { site, userProfile } = props;
    const { t } = useTranslation();
    const { exportVideoData, isLoading } = useVideoExportData({
        siteId: site.id,
        partnerId: site.partner.id,
    });
    const { logout } = useAuth();
    const {
        postVideoExportRequest,
        resetVideoExportUIStates,
        videoExportPostIsLoading,
        videoExportPostHasError,
        videoExportPostSuccess,
    } = useVideExportRequest({
        logout,
    });

    const { cameras, reasons } = exportVideoData || { cameras: [], reasons: [] };
    const [selectedCameras, setSelectedCameras] = useState<string[]>([]);
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
    const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
    const [selectedStartTime, setSelectedStartTime] = useState<Moment | null>(null);
    const [selectedEndTime, setSelectedEndTime] = useState<Moment | null>(null);
    const [notes, setNotes] = useState<string>("");
    const [showMessage, setShowMessage] = useState(false);
    const [message, setMessage] = useState("");
    const [messageSeverity, setMessageSeverity] = useState<Color | undefined>("info");
    const ampm = usesAmPm(userProfile.locale);

    const [showErrorCameras, setShowErrorCameras] = useState(false);
    const [errorCamerasMessage, setErrorCamerasMessage] = useState("");
    const [showErrorDateTime, setShowErrorDateTime] = useState(false);
    const [errorDateTimeMessage, setErrorDateTimeMessage] = useState("");
    const [showErrorReasons, setShowErrorReasons] = useState(false);
    const [errorReasonMessage, setErrorReasonMessage] = useState("");
    const [submittedOnce, setSubmittedOnce] = useState(false);

    const clearForm = () => {
        setSelectedReasons([]);
        setSelectedCameras([]);
        setSelectedDate(null);
        setSelectedStartTime(null);
        setSelectedEndTime(null);
        setNotes("");
    };

    const validateCamerasAtLeastOneSelected = () => {
        return selectedCameras.length > 0;
    };

    const validateCamerasAtMostThreeSelected = () => {
        return selectedCameras.length <= 3;
    };

    const validateDatesExisting = () => {
        return selectedDate && selectedStartTime && selectedEndTime;
    };

    const validateDatesOrder = () => {
        return selectedStartTime && selectedStartTime.isBefore(selectedEndTime, "minute");
    };

    const validateTimeLimits = () => {
        if (!selectedStartTime || !selectedEndTime) {
            return false;
        }
        return selectedEndTime.diff(selectedStartTime) <= 10800000; // max 3 hours
    };

    const validateEndTimeLimits = () => {
        if (!selectedStartTime || !selectedEndTime) {
            return false;
        }

        // Only allow video export requests that end before current hour
        // This is due to that Eos can't export video that is currently being recorded
        const localNow = moment().tz(site.timeZone);
        localNow.set({ minutes: 0, seconds: 0, milliseconds: 0 });
        const selectedEndTimeWithSiteTimezone = selectedEndTime.clone().tz(site.timeZone, true);
        return selectedEndTimeWithSiteTimezone.isBefore(localNow, "minute");
    };

    const validatReasonsSelected = () => {
        return selectedReasons.length > 0;
    };

    const validateNotes = () => {
        return notes.length > 0;
    };

    const getCamerasErrorMessage = () => {
        if (!validateCamerasAtLeastOneSelected()) {
            return "Entities.VideoExport.ErrorNoCameraSelected";
        }
        if (!validateCamerasAtMostThreeSelected()) {
            return "Entities.VideoExport.ErrorTooManyCamerasSelected";
        }

        return "";
    };

    const GetDateTimeErrorMessage = () => {
        if (!validateDatesExisting()) {
            return "Entities.VideoExport.DateAndTimeRequired";
        }
        if (!validateDatesOrder()) {
            return "Entities.VideoExport.ErrorStartTimeAfterEndTime";
        }
        if (!validateTimeLimits()) {
            return "Entities.VideoExport.ErrorTimeLimits";
        }
        if (!validateEndTimeLimits()) {
            return "Entities.VideoExport.ErrorEndTimeLimit";
        }

        return "";
    };

    const GetReasonErrorMessage = () => {
        if (!validatReasonsSelected()) {
            return "Entities.VideoExport.ErrorNoReasonSelected";
        }
        if (!validateNotes()) {
            return "Entities.VideoExport.ErrorNoNotes";
        }

        return "";
    };

    const SetTimeBasedOnSelectedDate = (date: Moment | null, time: Moment | null, setter: (value: Moment) => void) => {
        if (!time) {
            return;
        }

        if (date) {
            const tmpTime = time.clone();
            tmpTime.set({
                year: date.year(),
                month: date.month(),
                date: date.date(),
            });
            setter(tmpTime);
        } else {
            setter(time);
        }
    };

    useEffect(() => {
        if (videoExportPostHasError) {
            setMessage(t("Entities.VideoExport.ErrorExportRequest"));
            setMessageSeverity("error");
            setShowMessage(true);
            setSubmittedOnce(false);
            resetVideoExportUIStates();
        }
        if (videoExportPostSuccess) {
            setMessage(t("Entities.VideoExport.Success"));
            setMessageSeverity("success");
            setShowMessage(true);
            setSubmittedOnce(false);
            resetVideoExportUIStates();
            clearForm();
        }

        if (validateCamerasAtLeastOneSelected() && validateCamerasAtMostThreeSelected()) {
            setShowErrorCameras(false);
        } else if (submittedOnce) {
            setErrorCamerasMessage(getCamerasErrorMessage());
            setShowErrorCameras(true);
        }

        if (validatReasonsSelected() && validateNotes()) {
            setShowErrorReasons(false);
        } else if (submittedOnce) {
            setErrorReasonMessage(GetReasonErrorMessage());
            setShowErrorReasons(true);
        }

        if (validateDatesExisting() && validateDatesOrder() && validateTimeLimits() && validateEndTimeLimits()) {
            setShowErrorDateTime(false);
        } else if (submittedOnce) {
            setErrorDateTimeMessage(GetDateTimeErrorMessage());
            setShowErrorDateTime(true);
        }
    }, [
        videoExportPostHasError,
        videoExportPostSuccess,
        selectedCameras,
        selectedReasons,
        notes,
        selectedDate,
        selectedStartTime,
        selectedEndTime,
        submittedOnce,
    ]);

    useEffect(() => {
        // Set the date part of the start and end time to the selected date
        if (selectedDate) {
            SetTimeBasedOnSelectedDate(selectedDate, selectedStartTime, setSelectedStartTime);
            SetTimeBasedOnSelectedDate(selectedDate, selectedEndTime, setSelectedEndTime);
        }
    }, [selectedDate]);

    useEffect(() => {
        // Set a default end time if a start time is selected
        // The defuault end time is 1 hour after the start time
        if (!selectedEndTime && selectedStartTime) {
            setSelectedEndTime(selectedStartTime.clone().add(1, "hour"));
        }
    }, [selectedStartTime]);

    const onCameraSelected = (cameraId: string) => () => {
        if (selectedCameras.includes(cameraId)) {
            setSelectedCameras(selectedCameras.filter((id) => id !== cameraId));
        } else {
            setSelectedCameras([...selectedCameras, cameraId]);
        }
    };

    const onSubmit = async () => {
        setSubmittedOnce(true);
        setShowErrorCameras(false);
        setShowErrorDateTime(false);
        setShowErrorReasons(false);

        let error = false;
        if (!validateCamerasAtLeastOneSelected() || !validateCamerasAtMostThreeSelected()) {
            setShowErrorCameras(true);
            setErrorCamerasMessage(getCamerasErrorMessage());
            error = true;
        }

        if (!validateDatesExisting() || !validateDatesOrder() || !validateTimeLimits() || !validateEndTimeLimits()) {
            setShowErrorDateTime(true);
            setErrorDateTimeMessage(GetDateTimeErrorMessage());
            error = true;
        }

        if (!validatReasonsSelected() || !validateNotes()) {
            setShowErrorReasons(true);
            setErrorReasonMessage(GetReasonErrorMessage());
            error = true;
        }

        if (error) return;

        if (selectedDate && selectedStartTime && selectedEndTime) {
            const selectedDateWithSiteTimezone = moment.tz(selectedDate.format("YYYY-MM-DD"), site.timeZone);

            const startTime = selectedDateWithSiteTimezone.clone();
            startTime.add(selectedStartTime.hours(), "hours");
            startTime.add(selectedStartTime.minutes(), "minutes");

            const endTime = selectedDateWithSiteTimezone.clone();
            endTime.add(selectedEndTime.hours(), "hours");
            endTime.add(selectedEndTime.minutes(), "minutes");

            const sanitizedNotes = DOMPurify.sanitize(notes);

            const request: VideoExportPost = {
                siteId: site.id,
                cameraIds: selectedCameras,
                reasons: selectedReasons,
                startTimeUtc: startTime.utc(),
                stopTimeUtc: endTime.utc(),
                details: sanitizedNotes,
            };

            await postVideoExportRequest(request);
        }
    };

    return (
        <div className="video-export">
            <Fade in={isLoading || videoExportPostIsLoading} timeout={{ enter: 700, exit: 400 }}>
                <LinearProgress />
            </Fade>
            <Grid container direction="row" style={{ marginTop: "20px" }} spacing={3}>
                <Grid item xs={12}>
                    <Card
                        style={{ backgroundColor: "#252525", marginBottom: 0 }}
                        className={
                            showErrorCameras ? "video-export__form-error__box" : "video-export__form-normal__box"
                        }
                    >
                        <Box m={3}>
                            <Typography display="inline" variant="h6">
                                {t("Entities.VideoExport.SelectCameras")}
                            </Typography>
                        </Box>
                        <Box mt={2} mb={1}>
                            <List style={{ width: "100%", display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                                {!isLoading ? (
                                    cameras.length > 0 ? (
                                        cameras.map((camera) => (
                                            <ListItem
                                                key={camera.id}
                                                role={undefined}
                                                dense
                                                button
                                                onClick={
                                                    !isLoading && !videoExportPostIsLoading
                                                        ? onCameraSelected(camera.id)
                                                        : undefined
                                                }
                                                classes={{ root: "video-export__camera-list-item" }}
                                            >
                                                <ListItemIcon>
                                                    <Box ml={2}>
                                                        <Checkbox
                                                            edge="start"
                                                            disabled={isLoading || videoExportPostIsLoading}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            checked={selectedCameras.includes(camera.id)}
                                                        ></Checkbox>
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary={camera.name} />
                                            </ListItem>
                                        ))
                                    ) : (
                                        <Box mx={3} mb={2}>
                                            <Typography variant="body1">
                                                <i>{t("Gui.Common.NoRecords")}</i>
                                            </Typography>
                                        </Box>
                                    )
                                ) : (
                                    <>
                                        <Skeleton variant="rect" height={30} style={{ marginBottom: 10 }} />
                                        <Skeleton variant="rect" height={30} style={{ marginBottom: 10 }} />
                                        <Skeleton variant="rect" height={30} style={{ marginBottom: 10 }} />
                                    </>
                                )}
                            </List>
                        </Box>
                    </Card>
                    <Box>
                        <Collapse in={showErrorCameras}>
                            <Fade in={showErrorCameras}>
                                <Alert
                                    className="video-export__form-error__label"
                                    elevation={6}
                                    variant="outlined"
                                    severity="error"
                                >
                                    {t(errorCamerasMessage)}
                                </Alert>
                            </Fade>
                        </Collapse>
                    </Box>
                </Grid>
                <Grid item md={6} xs={12}>
                    <Card
                        className={
                            showErrorDateTime ? "video-export__form-error__box" : "video-export__form-normal__box"
                        }
                        style={{ backgroundColor: "#252525", marginBottom: 0, minHeight: 308 }}
                    >
                        <Box m={3}>
                            <Typography variant="h6">{t("Entities.VideoExport.SelectDateAndTime")}</Typography>
                        </Box>
                        <Box m={3}>
                            <MuiPickersUtilsProvider utils={MomentUtils} locale={"sv-se"}>
                                <Grid container direction="column">
                                    <Box mb={2} mt={0}>
                                        <DatePicker
                                            style={{ marginTop: 0 }}
                                            disableFuture
                                            value={selectedDate}
                                            label={t("Entities.VideoExport.SelectDate")}
                                            margin="normal"
                                            DialogProps={{ fullWidth: true }}
                                            onChange={(date: MaterialUiPickersDate) => {
                                                setSelectedDate(date);
                                            }}
                                            disabled={isLoading || videoExportPostIsLoading}
                                        />
                                    </Box>
                                    <Grid container direction="row">
                                        <Box m={2} marginLeft={0} marginRight={4}>
                                            <Grid container direction="column">
                                                <TimePicker
                                                    value={selectedStartTime}
                                                    label={t("Entities.VideoExport.StartTime")}
                                                    DialogProps={{ fullWidth: true }}
                                                    ampm={ampm}
                                                    onChange={(time: MaterialUiPickersDate) => {
                                                        SetTimeBasedOnSelectedDate(
                                                            selectedDate,
                                                            time,
                                                            setSelectedStartTime,
                                                        );
                                                    }}
                                                    disabled={isLoading || videoExportPostIsLoading}
                                                />
                                                <Typography className="video-export__timezone">
                                                    {selectedStartTime
                                                        ? moment(selectedStartTime)
                                                              .tz(site.timeZone)
                                                              .format("[(GMT] Z[)]")
                                                        : ""}
                                                </Typography>
                                            </Grid>
                                        </Box>
                                        <Box m={2} marginLeft={0}>
                                            <TimePicker
                                                value={selectedEndTime}
                                                label={t("Entities.VideoExport.EndTime")}
                                                DialogProps={{ fullWidth: true }}
                                                ampm={ampm}
                                                onChange={(time: MaterialUiPickersDate) => {
                                                    SetTimeBasedOnSelectedDate(selectedDate, time, setSelectedEndTime);
                                                }}
                                                disabled={isLoading || videoExportPostIsLoading}
                                            />
                                            <Typography className="video-export__timezone">
                                                {selectedEndTime
                                                    ? moment(selectedEndTime).tz(site.timeZone).format("[(GMT] Z[)]")
                                                    : ""}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </MuiPickersUtilsProvider>
                        </Box>
                    </Card>
                    <Box width={300}>
                        <Collapse in={showErrorDateTime}>
                            <Fade in={showErrorDateTime}>
                                <Alert
                                    className="video-export__form-error__label"
                                    elevation={6}
                                    variant="outlined"
                                    severity="error"
                                >
                                    {t(errorDateTimeMessage)}
                                </Alert>
                            </Fade>
                        </Collapse>
                    </Box>
                </Grid>
                <Grid item md={6} xs={12}>
                    <Card
                        className={
                            showErrorReasons ? "video-export__form-error__box" : "video-export__form-normal__box"
                        }
                        style={{ backgroundColor: "#252525", marginBottom: 0, minHeight: 308 }}
                    >
                        <Box m={3}>
                            <Typography variant="h6">{t("Entities.VideoExport.SelectReasons")}</Typography>
                        </Box>
                        <Box m={3} mb={5} pt={2}>
                            <Select
                                multiple
                                displayEmpty
                                value={selectedReasons}
                                onChange={(e) => {
                                    setSelectedReasons(e.target.value as string[]);
                                }}
                                renderValue={(selected) =>
                                    !!(selected as string[]).length ? (
                                        (selected as string[]).join(", ")
                                    ) : (
                                        <FormLabel>{t("Entities.VideoExport.SelectReasons")}</FormLabel>
                                    )
                                }
                                className="video-export__reason-select"
                                disabled={isLoading || videoExportPostIsLoading}
                            >
                                {reasons.map((reason) => (
                                    <MenuItem key={reason} value={reason}>
                                        <Checkbox checked={selectedReasons.includes(reason)} />
                                        <ListItemText primary={reason} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <Box m={3}>
                            <TextField
                                value={notes}
                                onChange={(e) => {
                                    setNotes(e.target.value);
                                }}
                                placeholder={t("Entities.VideoExport.OtherNotesPlaceholder")}
                                multiline
                                rows={4}
                                variant="outlined"
                                className="video-export__notes"
                                disabled={isLoading || videoExportPostIsLoading}
                            />
                        </Box>
                    </Card>
                    <Collapse in={showErrorReasons}>
                        <Fade in={showErrorReasons}>
                            <Alert
                                className="video-export__form-error__label"
                                elevation={6}
                                variant="outlined"
                                severity="error"
                            >
                                {t(errorReasonMessage)}
                            </Alert>
                        </Fade>
                    </Collapse>
                </Grid>
            </Grid>
            <Box mb={2} mt={4}>
                <FormControl>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            onSubmit();
                        }}
                        disabled={isLoading || videoExportPostIsLoading}
                    >
                        {t("Entities.VideoExport.SubmitButton")}
                    </Button>
                </FormControl>
            </Box>
            <Box my={2} mb={12}>
                <Snackbar
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    onClose={() => {
                        setShowMessage(false);
                        setMessage("");
                    }}
                    open={showMessage}
                >
                    <MuiAlert elevation={6} variant="filled" severity={messageSeverity}>
                        {t(message)}
                    </MuiAlert>
                </Snackbar>
            </Box>
        </div>
    );
};
